import { useState } from 'react';
import { CheckCircle, XCircle, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

type Scenario = {
  id: string; title: string; contextDescription: string;
  choices: { label: string; isCorrect: boolean; feedback: string }[];
  sortOrder: number;
};

function ScenarioCard({ scenario, index }: { scenario: Scenario; index: number }) {
  const [selected, setSelected] = useState<number | null>(null);
  const answered = selected !== null;
  const correct = answered && scenario.choices[selected]?.isCorrect;

  return (
    <div className="border border-border rounded-xl overflow-hidden">
      <div className="bg-muted/50 p-4 border-b border-border">
        <p className="text-xs font-bold text-primary uppercase tracking-wider mb-1">Scenario {index + 1}</p>
        <h3 className="text-sm font-bold text-foreground">{scenario.title}</h3>
        <p className="text-sm text-foreground/80 mt-2 leading-relaxed">{scenario.contextDescription}</p>
      </div>

      <div className="p-4 space-y-2">
        {scenario.choices.map((choice, i) => {
          const isSelected = selected === i;
          const showResult = answered;
          let cls = 'border border-border bg-card hover:bg-muted/50';
          if (showResult && choice.isCorrect) cls = 'border-2 border-primary bg-primary/5';
          else if (showResult && isSelected && !choice.isCorrect) cls = 'border-2 border-destructive bg-destructive/5';
          else if (isSelected) cls = 'border-2 border-primary bg-primary/10';

          return (
            <button key={i} onClick={() => !answered && setSelected(i)}
              disabled={answered}
              className={`w-full text-left rounded-lg p-3 flex items-start gap-3 transition-all ${cls}`}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                showResult && choice.isCorrect ? 'bg-primary text-primary-foreground' :
                showResult && isSelected ? 'bg-destructive text-destructive-foreground' :
                isSelected ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'
              }`}>
                {showResult && choice.isCorrect ? <CheckCircle className="w-3.5 h-3.5" /> :
                 showResult && isSelected ? <XCircle className="w-3.5 h-3.5" /> :
                 <HelpCircle className="w-3.5 h-3.5" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-foreground">{choice.label}</p>
                {showResult && isSelected && choice.feedback && (
                  <p className={`text-xs mt-2 leading-relaxed ${choice.isCorrect ? 'text-primary' : 'text-destructive'}`}>
                    {choice.feedback}
                  </p>
                )}
                {showResult && !isSelected && choice.isCorrect && choice.feedback && (
                  <p className="text-xs mt-2 leading-relaxed text-primary">{choice.feedback}</p>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {answered && (
        <div className={`px-4 pb-4`}>
          <div className={`rounded-lg p-3 text-sm ${correct ? 'bg-primary/10 text-primary' : 'bg-destructive/10 text-destructive'}`}>
            {correct ? '✓ Well done!' : '✗ Review the correct answer above.'}
          </div>
        </div>
      )}
    </div>
  );
}

export default function PractiseStep({ scenarios, onContinue }: {
  scenarios: Scenario[];
  onContinue: () => void;
}) {
  if (scenarios.length === 0) {
    return (
      <div className="space-y-4">
        <h2 className="text-lg font-bold text-foreground">Practice Scenarios</h2>
        <p className="text-sm text-muted-foreground">No scenarios configured for this module yet.</p>
        <Button onClick={onContinue} className="w-full h-12 text-base">Continue</Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-bold text-foreground">Practice Scenarios</h2>
      <p className="text-sm text-muted-foreground">Work through these real-world scenarios. Choose the best response for each situation.</p>

      <div className="space-y-4">
        {scenarios.map((s, i) => <ScenarioCard key={s.id} scenario={s} index={i} />)}
      </div>

      <Button onClick={onContinue} className="w-full h-12 text-base">
        Continue to Knowledge Check
      </Button>
    </div>
  );
}
